import { domainError, type DomainError } from '../shared/domain-error';
import type { UserId } from '../shared/ids';
import { err, ok, type Result } from '../shared/result';
import type { RoleAssignment } from './permission-resolver';
import type { PermissionKey } from './permissions';
import { ROLE_ERROR } from './role';
import { ROLE } from './roles';
import { canOperate, type User } from './user';

/**
 * O Super administrador: um titular só, e que não se perde (RN-286 a RN-290).
 *
 * O sistema precisa de alguém que decida o que cada papel abre — e de exatamente uma
 * pessoa responsável por isso, porque papel que muda de acordo com quem clicou por último
 * não é política de acesso. O banco garante o "no máximo um" (`0042`); aqui ficam as
 * regras que o banco não sabe: quem pode tocar no titular, e como o papel muda de mãos.
 */

/**
 * O perfil é o titular? Só o papel **global** conta: um Super administrador "em uma obra"
 * seria um contra-senso, e o caso de uso o recusa ao atribuir.
 */
export function holdsSuperAdmin(
  assignments: readonly RoleAssignment[],
  rolesById: ReadonlyMap<string, { readonly key: string }>,
): boolean {
  return assignments.some(
    (assignment) =>
      assignment.projectId === undefined &&
      rolesById.get(assignment.roleId)?.key === ROLE.SUPER_ADMIN,
  );
}

/**
 * Os papéis que a tela oferece ao atribuir. O de Super administrador não está entre eles
 * para ninguém — nem para o titular: ele só muda de mãos pela transferência, que tira de
 * um e dá ao outro numa operação só (RN-286).
 */
export function assignableRoles<T extends { readonly key: string }>(roles: readonly T[]): T[] {
  return roles.filter((role) => role.key !== ROLE.SUPER_ADMIN);
}

/**
 * Quem não é o titular não mexe nele, e ninguém o desativa (RN-287, RN-288).
 *
 * O titular edita o próprio cadastro e a própria senha — é a conta dele. O que ninguém
 * faz, nem ele, é desativar-se: o papel nunca fica sem dono, e quem quer sair passa o
 * papel adiante primeiro.
 */
export function protectSuperAdmin(input: {
  readonly actorId: UserId;
  readonly targetId: UserId;
  readonly targetIsSuperAdmin: boolean;
  readonly deactivating?: boolean;
}): Result<void, DomainError> {
  if (!input.targetIsSuperAdmin) return ok(undefined);

  if (input.actorId !== input.targetId) {
    return err(
      domainError(
        ROLE_ERROR.SUPER_ADMIN_PROTECTED,
        'Este perfil é o do Super administrador. Só ele mexe nele.',
      ),
    );
  }
  if (input.deactivating === true) {
    return err(
      domainError(
        ROLE_ERROR.SUPER_ADMIN_REQUIRED,
        'O Super administrador não se desativa: transfira o papel a outro perfil antes.',
      ),
    );
  }
  return ok(undefined);
}

/**
 * A transferência: do titular para outro perfil **ativo** (RN-289).
 *
 * Passar o papel a quem não pode operar o deixaria numa conta que ninguém usa — o sistema
 * continuaria com titular no papel e sem ninguém capaz de decidir.
 */
export function validateTransfer(input: {
  readonly actorId: UserId;
  readonly actorIsSuperAdmin: boolean;
  readonly target: User | undefined;
}): Result<void, DomainError> {
  if (!input.actorIsSuperAdmin) {
    return err(
      domainError(ROLE_ERROR.TRANSFER_INVALID, 'Só o Super administrador transfere o papel.'),
    );
  }
  if (input.target === undefined || input.target.deletedAt !== undefined) {
    return err(
      domainError(ROLE_ERROR.TRANSFER_INVALID, 'Escolha o perfil que vai receber o papel.'),
    );
  }
  if (input.target.id === input.actorId) {
    return err(
      domainError(ROLE_ERROR.TRANSFER_INVALID, 'O papel já é seu — escolha outro perfil.'),
    );
  }
  if (!canOperate(input.target)) {
    return err(
      domainError(
        ROLE_ERROR.TRANSFER_INVALID,
        'Este perfil está inativo. O papel só passa a quem pode operar o sistema.',
      ),
    );
  }
  return ok(undefined);
}

/**
 * As permissões do papel que quem concede **não tem** (RN-290).
 *
 * Sem este limite, quem atribui papéis (`users.manage`) criaria o próprio caminho até o
 * que não tem: bastaria o titular ter montado um papel com `roles.manage` e o
 * Administrador se atribuiria. Vazio = pode conceder.
 */
export function permissionsBeyond(
  rolePermissions: readonly PermissionKey[],
  grantorPermissions: ReadonlySet<PermissionKey>,
): PermissionKey[] {
  return rolePermissions.filter((permission) => !grantorPermissions.has(permission));
}
